import { ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/lib/utils';
import type { MenuItem } from '~/lib/menu';
import { useActiveMenuPath } from '~/hooks/use-active-menu-path';
import { useSidebarStore } from '~/lib/store/sidebar';
import { SidebarSubItem } from './sidebar-sub-item';
import { SidebarFlyout } from './sidebar-flyout';

function isChildActive(item: MenuItem, activePath: string | undefined): boolean {
	return (item.children ?? []).some(c => c.path === activePath);
}

/** A top-level menu row: leaf (navigates) or parent (accordion expanded / flyout collapsed). */
export function SidebarItem({ item }: { item: MenuItem }) {
	const { t } = useTranslation();
	const activePath = useActiveMenuPath();
	const collapsed = useSidebarStore(s => s.collapsed);
	const expanded = useSidebarStore(s => s.expandedItems.includes(item.id));
	const toggleExpanded = useSidebarStore(s => s.toggleExpanded);

	const Icon = item.icon;
	const hasChildren = !!item.children?.length;
	const leafActive = !!item.path && item.path === activePath;
	const childActive = hasChildren && isChildActive(item, activePath);
	const inheritedActive = childActive && !expanded;

	// --- Collapsed (64px) ---
	if (collapsed) {
		if (hasChildren) {
			return <SidebarFlyout item={item} active={childActive} />;
		}
		// Leaf: icon-only with a tooltip.
		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<Link
						to={item.path!}
						aria-label={t(item.labelKey)}
						className={cn(
							'flex h-7 w-full items-center justify-center rounded-md transition-colors',
							leafActive ? 'border-l-[3px] border-l-brand bg-brand-subtle' : 'hover:bg-muted'
						)}>
						<Icon size={16} className={cn('shrink-0', leafActive ? 'text-brand-subtle-fg' : 'text-foreground-subtle')} />
					</Link>
				</TooltipTrigger>
				<TooltipContent side="right">{t(item.labelKey)}</TooltipContent>
			</Tooltip>
		);
	}

	// --- Expanded (220px) ---
	const rowClass = cn(
		'flex h-7 w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[12.5px] transition-colors',
		leafActive
			? 'bg-brand-subtle font-medium text-brand-subtle-fg'
			: inheritedActive
				? 'font-medium text-brand-subtle-fg hover:bg-muted'
				: 'font-normal text-foreground hover:bg-muted'
	);
	const iconClass = leafActive || inheritedActive ? 'text-brand-subtle-fg' : 'text-foreground-subtle';

	if (!hasChildren && item.path) {
		return (
			<Link to={item.path} className={rowClass}>
				<Icon size={16} className={cn('shrink-0', iconClass)} />
				<span className="truncate">{t(item.labelKey)}</span>
			</Link>
		);
	}

	return (
		<div>
			<button type="button" onClick={() => toggleExpanded(item.id)} className={rowClass}>
				<Icon size={16} className={cn('shrink-0', iconClass)} />
				<span className="flex-1 truncate text-left">{t(item.labelKey)}</span>
				{expanded ? (
					<ChevronDown size={11} className="shrink-0 text-foreground-subtle" />
				) : (
					<ChevronRight size={11} className="shrink-0 text-foreground-subtle" />
				)}
			</button>
			{expanded && (
				<div className="mt-0.5 flex flex-col">
					{item.children?.map(child => (
						<SidebarSubItem key={child.id} child={child} />
					))}
				</div>
			)}
		</div>
	);
}
